// src/components/PrintableClientReport.js - الإصدار المصحح
import React, { forwardRef } from 'react';
import styles from './PrintableClientReport.module.css';
import { formatNumber } from '../../utils/formatters';

// ==========================================================
// الدوال المساعدة - تم إضافتها هنا
// ==========================================================

const getStatusInArabic = (status) => {
    const statusMap = { 
        'completed': 'مكتمل', 
        'in_progress': 'قيد التنفيذ', 
        'confirmed': 'مؤكد', 
        'pending': 'قيد الانتظار', 
        'cancelled': 'ملغي' 
    };
    return statusMap[status] || status;
};

const formatDate = (dateStr) => {
    if (!dateStr) return 'غير محدد';
    try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('ar-IQ', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    } catch (error) {
        return dateStr;
    }
};

const getBookingTypeDisplay = (type) => {
    const types = {
        graduation: 'حفلات التخرج',
        kids: 'تصوير أطفال',
        event: 'مناسبات',
        wedding: 'الأعراس',
        special: 'طلب خاص',
        digital_marketing: 'تسويق إلكتروني'
    };
    return types[type] || type;
};

// ==========================================================
// المكون الرئيسي
// ==========================================================

const PrintableClientReport = forwardRef(({ reportData, filters, clientName, settings, includeExpenses }, ref) => {
    if (!reportData || !settings) {
        return null;
    }

    const { financial_summary, bookings } = reportData;

    // دالة للتحكم في عرض الحجوزات والجداول
    const shouldAvoidPageBreak = (booking, index) => {
        // إذا كان الحجز يحتوي على جداول صغيرة، امنع انقطاعه
        const hasSmallTables = 
            (booking.expenses?.length > 0 && booking.expenses.length <= 3) ||
            (booking.payments?.length > 0 && booking.payments.length <= 3);
        
        return hasSmallTables || index === bookings.length - 1;
    };

    return (
        <div ref={ref} className={styles.printContainer}>
            
            <header className={styles.printHeader}>
                {settings.banner && (
                    <img 
                        src={settings.banner} 
                        alt="Company Banner" 
                        className={styles.bannerImage}
                    />
                )}
            </header>

            <main className={styles.printContent}>
                <h1 className={styles.mainTitle}>كشف حساب العميل</h1>
                <h2 className={styles.subtitle}>{clientName}</h2>
                
                <div className={styles.filterInfo}>
                    <span><strong>من:</strong> {filters.start_date ? formatDate(filters.start_date) : 'البداية'}</span>
                    <span><strong>إلى:</strong> {filters.end_date ? formatDate(filters.end_date) : 'النهاية'}</span>
                    <span style={{ gridColumn: '1 / -1' }}>
                        <strong>تاريخ التقرير:</strong> {new Date().toLocaleDateString('ar-IQ')}
                    </span>
                </div>

                {/* قسم الملخص المالي */}
                <section className={styles.section}>
                    <h2 className={styles.sectionTitle}>ملخص الحساب</h2>
                    <div className={styles.financialGrid}>
                        <div className={`${styles.summaryCard} ${styles.total}`}>
                            <span className={styles.cardTitle}>إجمالي قيمة الحجوزات</span>
                            <span className={styles.cardValue}>{formatNumber(financial_summary.total_bookings_value, 0)} د.ع</span>
                        </div>
                        <div className={`${styles.summaryCard} ${styles.paid}`}>
                            <span className={styles.cardTitle}>إجمالي المبالغ المدفوعة</span>
                            <span className={styles.cardValue}>{formatNumber(financial_summary.total_payments_received, 0)} د.ع</span>
                        </div>
                        <div className={`${styles.summaryCard} ${styles.pending}`}>
                            <span className={styles.cardTitle}>المبلغ المتبقي</span>
                            <span className={styles.cardValue}>{formatNumber(financial_summary.remaining_balance, 0)} د.ع</span>
                        </div>
                    </div>
                </section>

                {/* قسم تفاصيل الحجوزات مع تحسينات التحكم في الصفحات */}
                <section className={styles.section}>
                    <h2 className={styles.sectionTitle}>تفاصيل الحجوزات ({bookings.length})</h2>
                    
                    {bookings.length > 0 ? (
                        bookings.map((booking, index) => (
                            <div 
                                key={booking.id} 
                                className={styles.bookingGroup}
                                style={{
                                    // التحكم الديناميكي في انقطاع الصفحات
                                    pageBreakInside: shouldAvoidPageBreak(booking, index) ? 'avoid' : 'auto'
                                }}
                            >
                                <h3 className={styles.bookingTitle}>
                                    الحجز #{index + 1}: {getBookingTypeDisplay(booking.booking_type)} بتاريخ {formatDate(booking.booking_date)}
                                </h3>

                                <div className={styles.bookingDetails}>
                                    <span className={styles.detailItem}>
                                        <strong>الحالة:</strong> {getStatusInArabic(booking.status)}
                                    </span>
                                    <span className={styles.detailItem}>
                                        <strong>الإجمالي:</strong> {formatNumber(booking.total_price, 0)} د.ع
                                    </span>
                                    <span className={styles.detailItem}>
                                        <strong>المدفوع:</strong> {formatNumber(booking.total_paid, 0)} د.ع
                                    </span>
                                    <span className={styles.detailItem}>
                                        <strong>المتبقي:</strong> {formatNumber(booking.remaining_amount, 0)} د.ع
                                    </span>
                                </div>

                                {includeExpenses && booking.expenses?.length > 0 && (
                                    <div className={styles.subTableContainer}>
                                        <h4 className={styles.subTableTitle}>المصاريف المرتبطة ({booking.expenses.length})</h4>
                                        <table className={styles.reportTable}>
                                            <thead>
                                                <tr>
                                                    <th style={{width: '25%'}}>الحساب</th>
                                                    <th style={{width: '35%'}}>الوصف</th>
                                                    <th style={{width: '20%'}}>التاريخ</th>
                                                    <th style={{width: '20%'}}>المبلغ</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {booking.expenses.map(expense => (
                                                    <tr key={expense.id}>
                                                        <td>{expense.category_name || 'غير محدد'}</td>
                                                        <td>{expense.description || '-'}</td>
                                                        <td>{formatDate(expense.expense_date)}</td>
                                                        <td className={styles.amountCell} style={{ color: '#dc2626' }}>
                                                            {formatNumber(expense.amount, 0)} د.ع
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {booking.payments?.length > 0 && (
                                    <div className={styles.subTableContainer}>
                                        <h4 className={styles.subTableTitle}>الدفعات المسجلة ({booking.payments.length})</h4>
                                        <table className={styles.reportTable}>
                                            <thead>
                                                <tr>
                                                    <th style={{width: '25%'}}>التاريخ</th>
                                                    <th style={{width: '25%'}}>المبلغ</th>
                                                    <th style={{width: '50%'}}>ملاحظات</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {booking.payments.map(payment => (
                                                    <tr key={payment.id}>
                                                        <td>{formatDate(payment.payment_date)}</td>
                                                        <td className={styles.amountCell} style={{ color: '#059669' }}>
                                                            {formatNumber(payment.amount, 0)} د.ع
                                                        </td>
                                                        <td>{payment.notes || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div style={{
                            textAlign: 'center', 
                            padding: '20px', 
                            color: '#64748b',
                            border: '1px dashed #e2e8f0',
                            borderRadius: '4px',
                            marginTop: '10px'
                        }}>
                            <p style={{ margin: '0', fontSize: '10pt' }}>
                                لا توجد حجوزات في هذه الفترة.
                            </p>
                        </div>
                    )}
                </section>

                {/* ملاحظات إضافية */}
                <section className={styles.section} style={{ 
                    marginBottom: '20px',
                    pageBreakBefore: 'avoid'
                }}>
                    <h3 className={styles.sectionTitle} style={{ fontSize: '10pt' }}>ملاحظات</h3>
                    <div style={{ fontSize: '8pt', color: '#64748b', lineHeight: '1.5' }}>
                        <p>• هذا التقرير يوضح ملخص الحساب والحركات المالية للعميل.</p>
                        <p>• يشمل جميع الحجوزات والمدفوعات والمصاريف ضمن الفترة المحددة.</p>
                        <p>• الأرقام بالدينار العراقي (د.ع).</p>
                        <p>• تم إنشاء التقرير آلياً بتاريخ {new Date().toLocaleDateString('ar-IQ')}.</p>
                    </div>
                </section>
            </main>

            <footer className={styles.printFooter}>
                <div className={styles.footerContent}>
                    {settings.company_name && <p>{settings.company_name}</p>}
                    {settings.phone_number && <p>{settings.phone_number}</p>}
                    {settings.address && <p>{settings.address}</p>}
                    <p>صفحة {1} من {1}</p>
                </div>
            </footer>
        </div>
    );
});

PrintableClientReport.displayName = 'PrintableClientReport';
export default PrintableClientReport;